import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getDeviceStatus, type DeviceStatus } from '@/lib/callables';

export interface DeviceStatusState {
  /** Per-device status keyed by deviceId. Absent id = not yet loaded. */
  byId: Record<string, DeviceStatus>;
  /** Offline threshold from the server (shared with the alert email). */
  thresholdMinutes: number;
}

// Used only until the first response lands; the server is the source of truth.
const DEFAULT_THRESHOLD_MINUTES = 30;

/**
 * Connection status for an account's buttons, via the `get_device_status`
 * callable. Polls every 60s while Settings is open and refetches when the
 * caregiver returns to the tab — React Query pauses the interval while the
 * tab is backgrounded, so a hidden tab makes no calls. No manual refresh: the
 * poll plus focus-refetch keep the indicator current on their own.
 */
export function useDeviceStatus(aid: string | null): DeviceStatusState {
  const { data } = useQuery({
    queryKey: ['deviceStatus', aid],
    queryFn: async () => {
      const result = await getDeviceStatus({ accountId: aid! });
      return result.data;
    },
    enabled: !!aid,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  const byId = useMemo(() => {
    const map: Record<string, DeviceStatus> = {};
    for (const d of data?.devices ?? []) map[d.deviceId] = d;
    return map;
  }, [data]);

  return {
    byId,
    thresholdMinutes: data?.thresholdMinutes ?? DEFAULT_THRESHOLD_MINUTES,
  };
}
